import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import type { DefaultFormModel } from 'shared/types/formkit';
import type {
	BaseViewModel,
	Question,
	ConsentForm,
	PersonalInfoBaseVM,
	Scalar,
	SendRequestAnswer,
} from './HealthCheckTool.types';

type StepKind = 'intro' | 'question' | 'consent' | 'personal';
type HealthCheckQuestions = Question & {
	index: number;
	title?: string;
	isValid: boolean;
	isTouched: boolean;
};
type HealthCheckPayload = {
	language: string | null;
	pageId: Scalar | null;
	questions: {
		questionBlockId: Scalar;
		answers: SendRequestAnswer | SendRequestAnswer[];
	}[];
	code: string;
	consentAccepted: boolean;
	form?: {
		formFields: Record<string, unknown>;
	};
};
type ExtendedSendRequestAnswer = SendRequestAnswer & {
	answerBlockId?: Scalar;
};
type HealthCheckState = {
	currentStep: number;
	steps: StepKind[];
	questions: HealthCheckQuestions[];
	personalInfoBaseVM: {
		baseViewModel?: PersonalInfoBaseVM;
	};
	currentQuestionStep: number;
	currentQuestion: Question | undefined;
	answersByQuestionId: Record<
		Scalar,
		ExtendedSendRequestAnswer | ExtendedSendRequestAnswer[]
	>;
	healthCheckPayload: HealthCheckPayload;
	isIntroStep: boolean;
	isQuestionStep: boolean;
	isConsentStep: boolean;
	isPersonalInfoStep: boolean;
	isBusy: boolean;
	checkTrackingName: string;
	consentForm?: ConsentForm;
	formErrors: {
		generalError: string;
		fieldErrors: Record<string, string | string[]>;
	};
};

const state: HealthCheckState = reactive({
	currentStep: 0,
	steps: [] as StepKind[],
	questions: [] as HealthCheckQuestions[],
	answersByQuestionId: {},
	healthCheckPayload: {
		language: null,
		pageId: null,
		questions: computed(() =>
			Object.entries(state.answersByQuestionId).map(
				([questionBlockId, answers]) => ({
					questionBlockId: Number(questionBlockId),
					answers: Array.isArray(answers)
						? answers.map((answer) => ({
								value: answer.value,
								weight: answer.weight,
							}))
						: [{ value: answers.value, weight: answers.weight }],
				}),
			),
		),
		code: '',
		consentAccepted: false,
	},
	currentQuestionStep: computed(
		() => state.currentStep - (state.steps.includes('intro') ? 1 : 0),
	),
	currentQuestion: computed(() => state.questions[state.currentQuestionStep]),
	personalInfoBaseVM: {
		baseViewModel: undefined,
	},
	isIntroStep: computed(() => state.steps[state.currentStep] === 'intro'),
	isQuestionStep: computed(() => state.steps[state.currentStep] === 'question'),
	isConsentStep: computed(() => state.steps[state.currentStep] === 'consent'),
	isPersonalInfoStep: computed(
		() => state.steps[state.currentStep] === 'personal',
	),
	checkTrackingName: '',
	consentForm: {},
	isBusy: false,
	formErrors: {
		generalError: '',
		fieldErrors: {},
	},
});

function initializeSteps(model: BaseViewModel) {
	const store = useStore();

	state.steps = [
		model.displayIntroPage && 'intro',
		...(model.questions ?? []).map(() => 'question'),
		model.consentFormEnabled && model.consentForm && 'consent',
		model.personalInformationFormEnabled && 'personal',
	].filter(Boolean) as StepKind[];
	state.questions = (model?.questions ?? []).map((question, index) => ({
		...question,
		index,
		title: question.questionText,
		isValid: false,
		isTouched: false,
	}));
	state.checkTrackingName = model.checkTrackingName;
	state.consentForm = model.consentForm;
	state.personalInfoBaseVM = {
		baseViewModel: {
			...(model.formModel ?? ({} as DefaultFormModel)),
			labels: {
				close: model.closeButtonText,
			},
		},
	};
	state.healthCheckPayload.pageId = model.pageId;
	state.healthCheckPayload.language = store.getters['getCulture'];

	if (state.currentStep >= state.steps.length) {
		state.currentStep = 0;
	}
}

function prefillAnswers() {
	state.answersByQuestionId = Object.fromEntries(
		state.questions.map((q) => [
			q.questionBlockId,
			q.options
				.filter((o) => o.selected)
				.map((o) => ({ value: o.value, weight: o.weight })),
		]),
	);
}

function setAnswer({
	questionBlockId,
	answer,
	notCollect = false,
}: {
	questionBlockId: Scalar;
	answer: ExtendedSendRequestAnswer | ExtendedSendRequestAnswer[];
	notCollect?: boolean;
}) {
	if (notCollect) {
		state.answersByQuestionId[questionBlockId] = [];
		updateStepValidity(questionBlockId, false);
		return;
	}

	if (Array.isArray(answer)) {
		state.answersByQuestionId[questionBlockId] = answer.map((a) => ({
			answerBlockId: a.answerBlockId,
			value: a.value,
			weight: a.weight,
		}));
		updateStepValidity(questionBlockId, answer.length > 0);
		return;
	}

	state.answersByQuestionId[questionBlockId] = [
		{
			answerBlockId: answer.answerBlockId,
			value: answer.value,
			weight: answer.weight,
		},
	];
	updateStepValidity(questionBlockId, !!answer.value);
}

function updateStepValidity(questionBlockId: Scalar, isValid: boolean) {
	const currentQuestion = state.questions.find(
		(question) => question.questionBlockId === questionBlockId,
	);

	if (currentQuestion) {
		currentQuestion.isValid = isValid;
	}
}

function nextStep() {
	if (state.currentStep < state.steps.length - 1) {
		state.currentStep += 1;
	}
}

function prevStep() {
	if (state.currentStep > 0) {
		state.currentStep -= 1;
	}
}

function goToStep(index: number) {
	if (index >= 0 && index < state.steps.length) {
		state.currentStep = index;
	}
}

function resetState() {
	state.currentStep = 0;
	state.steps = [];
	state.questions = [];
	state.answersByQuestionId = {};
	state.formErrors = {
		generalError: '',
		fieldErrors: {},
	};
}

const currentQuestionId = computed(() => {
	return state.questions[state.currentStep]?.questionBlockId ?? null;
});

const setHealthCheckPayload = (
	data: Partial<typeof state.healthCheckPayload>,
) => {
	state.healthCheckPayload = { ...state.healthCheckPayload, ...data };
};

const setFormErrors = (errors: {
	generalError: string;
	fieldErrors: Record<string, string | string[]>;
}) => {
	state.formErrors = {
		...state.formErrors,
		generalError: errors.generalError,
		fieldErrors: { ...state.formErrors.fieldErrors, ...errors.fieldErrors },
	};
};

export function useHealthCheckState() {
	return {
		state,
		currentQuestionId,
		initializeSteps,
		prefillAnswers,
		setAnswer,
		nextStep,
		prevStep,
		goToStep,
		resetState,
		setHealthCheckPayload,
		setFormErrors,
	};
}

